/**
 * Analytics: Plausible (KVKK-friendly) and optional GA4 when configured.
 * Only loads when the user has consented to analytics cookies.
 */

import { env } from '../lib/env';
import { isAnalyticsAllowed } from '../utils/cookieUtils';

declare global {
  interface Window {
    gtag?: (...args: unknown[]) => void;
    dataLayer?: unknown[];
    plausible?: (
      event: string,
      options?: { props?: Record<string, unknown>; u?: string }
    ) => void;
  }
}

let analyticsInitialized = false;

export const initializeAnalytics = (): void => {
  if (!isAnalyticsAllowed() || analyticsInitialized) {
    return;
  }
  initializePlausible();
  initializeGoogleAnalytics();
  analyticsInitialized = true;
};

export const initializePlausible = (): void => {
  if (!isAnalyticsAllowed()) {
    return;
  }

  const domain = env.plausibleDomain;
  if (!domain || document.querySelector('script[data-domain]')) {
    return;
  }

  const script = document.createElement('script');
  script.defer = true;
  script.dataset.domain = domain;
  script.src = 'https://plausible.io/js/script.manual.js';
  document.head.appendChild(script);
};

export const initializeGoogleAnalytics = (): void => {
  if (!isAnalyticsAllowed() || window.gtag) {
    return;
  }

  const gaId = env.gaMeasurementId;
  if (!gaId) {
    return;
  }

  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${gaId}`;
  document.head.appendChild(script);

  window.dataLayer = window.dataLayer || [];
  window.gtag = function (...args: unknown[]) {
    window.dataLayer?.push(args);
  };
  window.gtag('js', new Date());
  window.gtag('config', gaId, {
    anonymize_ip: true,
    cookie_flags: 'SameSite=None;Secure',
  });
};

export const trackPageView = (path: string): void => {
  if (!isAnalyticsAllowed()) {
    return;
  }

  if (window.plausible) {
    window.plausible('pageview', { u: path });
  }

  if (window.gtag && env.gaMeasurementId) {
    window.gtag('config', env.gaMeasurementId, { page_path: path });
  }
};

export const trackEvent = (
  eventName: string,
  eventParams?: Record<string, unknown>
): void => {
  if (!isAnalyticsAllowed()) {
    return;
  }

  if (window.plausible) {
    window.plausible(eventName, { props: eventParams });
  }

  if (window.gtag) {
    window.gtag('event', eventName, eventParams);
  }
};

export const disableAnalytics = (): void => {
  document
    .querySelectorAll('script[src*="googletagmanager.com"], script[src*="plausible.io"]')
    .forEach((script) => script.remove());

  if (window.dataLayer) {
    window.dataLayer = [];
  }
  delete window.gtag;
  delete window.plausible;
  analyticsInitialized = false;
};
